import { useState, useEffect, useRef } from "react";
import "../../pages/AboutPage.css";
import "./AboutPage.css";
import Avatar  from '../../Images/new_pic_6.png';
import Avatar1 from '../../Images/s1.jpeg';

/* ─── API Config ─── */
const BASE_URL = "https://staging.pteshstudios.com";

const API_BASE = `${BASE_URL}/api/about`;
const UPLOAD_URL = `${BASE_URL}/api/upload`;
const SERVER_ORIGIN = BASE_URL;

/* ─── Default data ─── */
const DEFAULT_DATA = {
  aboutUs: {
    heading: "Jeyshawn Engineering Limited is a leading indigenous Engineering, Procurement, Construction, Installation, and Commissioning (EPCIC) company dedicated to providing high-quality engineering and technical solutions to the oil & gas, energy, marine, manufacturing, and infrastructure sectors.",
    paragraphs: [
      "Established with a commitment to excellence and innovation, we have built a reputation for delivering complex projects with precision, efficiency, and professionalism. Our company was founded on the principle of creating dependable engineering solutions that support industrial development, operational growth, and sustainable progress across various sectors of the economy.",
      "With years of combined industry experience, our management team and technical professionals possess the expertise required to handle challenging projects in both onshore and offshore environments. We continuously invest in modern technologies, workforce development, and operational systems that enable us to deliver exceptional services that align with international best practices and client expectations.",
      "At Jeyshawn Engineering Limited, we understand that successful project delivery requires more than technical knowledge. It requires strategic planning, innovation, collaboration, effective communication, and an unwavering commitment to quality and safety. This understanding drives our approach to every project we undertake.",
      "Our integrated EPCIC solution covers the entire project lifecycle from initial planning, engineering studies, and procurement to construction, installation, commissioning, operations support, and maintenance services. By providing comprehensive solutions under one platform, we help our clients minimize operational risks, improve efficiency, reduce project timelines and maximize value.",
    ],
    image: "",
  },
  mission: {
    title: "OUR MISSION",
    text:  "At Jeyshawn, our mission is to execute projects safely, on time, and within budget, while exceeding client's expectations through engineering excellence, operational efficiency, and technological innovations.",
  },
  vision: {
    title: "OUR VISION",
    text:  "To be the most trusted partner in delivering world-class EPCIC solutions for sustainable and efficient energy infrastructure.",
  },
  bannerImage: "",
};

const resolveUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${SERVER_ORIGIN}${url}`;
};

/* ═══════════════════════════════
   IMAGE UPLOAD FIELD
═══════════════════════════════ */
function ImageUploadField({ label, currentUrl, onUploaded }) {
  const [preview, setPreview]     = useState(resolveUrl(currentUrl) || null);
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState(null);
  const inputRef = useRef();

  useEffect(() => { setPreview(resolveUrl(currentUrl) || null); }, [currentUrl]);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setError(null);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res  = await fetch(UPLOAD_URL, { method: "POST", body: formData });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || "Upload failed");
      onUploaded(json.url);
      setPreview(resolveUrl(json.url));
    } catch (err) {
      setError("Upload failed. Try again.");
      console.error("Upload error:", err);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    onUploaded("");
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="editor-img-field">
      <label>{label}</label>
      {preview ? (
        <div className="editor-img-preview-wrap">
          <img src={preview} alt="preview" className="editor-img-preview" />
          {uploading && (
            <div className="editor-img-uploading">
              <div className="editor-loading__spinner editor-loading__spinner--sm" />
              <span>Uploading...</span>
            </div>
          )}
          {!uploading && (
            <div className="editor-img-actions">
              <button type="button" className="editor-img-btn editor-img-btn--change" onClick={() => inputRef.current?.click()}>Change</button>
              <button type="button" className="editor-img-btn editor-img-btn--remove" onClick={handleRemove}>Remove</button>
            </div>
          )}
        </div>
      ) : (
        <button type="button" className="editor-img-dropzone" onClick={() => inputRef.current?.click()} disabled={uploading}>
          {uploading
            ? <><div className="editor-loading__spinner editor-loading__spinner--sm" /><span>Uploading...</span></>
            : <><span className="editor-img-dropzone__icon">🖼️</span><span>Click to upload image</span><span className="editor-img-dropzone__hint">JPG, PNG, WEBP — max 10 MB</span></>
          }
        </button>
      )}
      {error && <p className="editor-img-error">{error}</p>}
      <input ref={inputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFile} />
    </div>
  );
}

/* ═══════════════════════════════
   EDIT MODAL
═══════════════════════════════ */
function EditModal({ data, onSave, onClose, saving }) {
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(data)));

  const setParagraph = (i, v) =>
    setDraft(d => {
      const paragraphs = [...d.aboutUs.paragraphs];
      paragraphs[i] = v;
      return { ...d, aboutUs: { ...d.aboutUs, paragraphs } };
    });

  const addParagraph    = () => setDraft(d => ({ ...d, aboutUs: { ...d.aboutUs, paragraphs: [...d.aboutUs.paragraphs, ""] } }));
  const removeParagraph = (i) => setDraft(d => { const paragraphs = d.aboutUs.paragraphs.filter((_, idx) => idx !== i); return { ...d, aboutUs: { ...d.aboutUs, paragraphs } }; });

  return (
    <div className="editor-overlay" onClick={onClose}>
      <div className="editor-modal" onClick={e => e.stopPropagation()}>

        <div className="editor-modal__header">
          <h2>Edit About Page Content</h2>
          <button className="editor-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="editor-modal__body">

          {/* ── BANNER IMAGE ── */}
          <p className="editor-section__title">Banner Image</p>
          <div className="editor-group">
            <ImageUploadField
              label="Top Banner Image"
              currentUrl={draft.bannerImage}
              onUploaded={url => setDraft(d => ({ ...d, bannerImage: url }))}
            />
          </div>

          {/* ── ABOUT US ── */}
          <p className="editor-section__title">About Us Section</p>
          <div className="editor-group">
            <label>Opening Heading</label>
            <textarea rows={4} value={draft.aboutUs.heading} onChange={e => setDraft(d => ({ ...d, aboutUs: { ...d.aboutUs, heading: e.target.value } }))} />
            <ImageUploadField
              label="About Us Image"
              currentUrl={draft.aboutUs.image}
              onUploaded={url => setDraft(d => ({ ...d, aboutUs: { ...d.aboutUs, image: url } }))}
            />
          </div>
          <div className="editor-group">
            <p className="editor-group__label">Body Paragraphs</p>
            {draft.aboutUs.paragraphs.map((para, i) => (
              <div key={i} className="editor-value-row">
                <textarea
                  rows={4}
                  value={para}
                  onChange={e => setParagraph(i, e.target.value)}
                  style={{ flex: 1, padding: "9px 12px", border: "1px solid #d1d5db", borderRadius: 7, fontSize: 14, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }}
                />
                <button className="editor-value-row__remove" onClick={() => removeParagraph(i)} title="Remove">✕</button>
              </div>
            ))}
            <button className="editor-add-btn" onClick={addParagraph}>+ Add Paragraph</button>
          </div>

          {/* ── MISSION ── */}
          <p className="editor-section__title">Mission</p>
          <div className="editor-group">
            <label>Title</label>
            <input value={draft.mission.title} onChange={e => setDraft(d => ({ ...d, mission: { ...d.mission, title: e.target.value } }))} />
            <label>Text</label>
            <textarea rows={4} value={draft.mission.text} onChange={e => setDraft(d => ({ ...d, mission: { ...d.mission, text: e.target.value } }))} />
          </div>

          {/* ── VISION ── */}
          <p className="editor-section__title">Vision</p>
          <div className="editor-group">
            <label>Title</label>
            <input value={draft.vision.title} onChange={e => setDraft(d => ({ ...d, vision: { ...d.vision, title: e.target.value } }))} />
            <label>Text</label>
            <textarea rows={3} value={draft.vision.text} onChange={e => setDraft(d => ({ ...d, vision: { ...d.vision, text: e.target.value } }))} />
          </div>

        </div>

        <div className="editor-modal__footer">
          <button className="btn btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn--orange" onClick={() => onSave(draft)} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>

      </div>
    </div>
  );
}

/* ═══════════════════════════════
   SUB-COMPONENTS
═══════════════════════════════ */
function AboutUs({ data }) {
  const img = resolveUrl(data.aboutUs.image) || Avatar;
  return (
    <section className="about-us">
      <div className="about-us__header">
        <h2 className="section-title">About Us</h2>
        <span className="section-divider" />
      </div>
      <div className="about-us__body">
        <div className="about-us__img-wrap">
          <div className="about-us__img-accent" />
          <img src={img} alt="Offshore oil platform with workers" className="about-us__img" />
        </div>
        <div className="about-us__text">
          <div className="about_us_trans">
            <h2>{data.aboutUs.heading}</h2>
            {data.aboutUs.paragraphs.map((para, i) => <p key={i}>{para}</p>)}
          </div>
        </div>
      </div>
    </section>
  );
}

function IMSPolicy({ data }) {
  return (
    <section className="ims-policy">
      <div className="ims-policy__inner">
        <h2 className="ims-policy__title">{data.mission.title}</h2>
        <p className="ims-policy__text">{data.mission.text}</p>
      </div>
      <div className="ims-policy__inner">
        <h2 className="ims-policy__title">{data.vision.title}</h2>
        <p className="ims-policy__text">{data.vision.text}</p>
      </div>
    </section>
  );
}

/* ═══════════════════════════════
   MAIN EXPORT
═══════════════════════════════ */
export default function AboutPage() {
  const [pageData, setPageData]   = useState(DEFAULT_DATA);
  const [loading, setLoading]     = useState(true);
  const [fetchErr, setFetchErr]   = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [saveMsg, setSaveMsg]     = useState(null);

  useEffect(() => { fetchPageData(); }, []);

  const fetchPageData = async () => {
    try {
      setLoading(true);
      setFetchErr(null);
      const res  = await fetch(API_BASE);
      const json = await res.json();
      if (json.success) setPageData(json.data);
      else throw new Error(json.message || "Failed to fetch");
    } catch (err) {
      console.error("Fetch error:", err);
      setFetchErr("Could not load page data. Using defaults.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (draft) => {
    try {
      setSaving(true);
      const res  = await fetch(API_BASE, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(draft) });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || "Save failed");
      await fetchPageData();
      setModalOpen(false);
      showToast("Changes saved successfully!");
    } catch (err) {
      console.error("Save error:", err);
      showToast("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const showToast = (msg) => { setSaveMsg(msg); setTimeout(() => setSaveMsg(null), 3500); };

  const bannerImg = resolveUrl(pageData.bannerImage) || Avatar1;

  return (
    <>
      {/* Edit FAB */}
      <button className="edit-fab" onClick={() => setModalOpen(true)} title="Edit page content">
        <span className="edit-fab__icon">✏️</span>
        <span className="edit-fab__label">Edit Page</span>
      </button>

      {saveMsg && <div className="editor-toast">{saveMsg}</div>}
      {loading && <div className="editor-loading"><div className="editor-loading__spinner" /></div>}
      {fetchErr && (
        <div className="editor-error-banner">
          {fetchErr}
          <button onClick={fetchPageData}>Retry</button>
        </div>
      )}
      {modalOpen && (
        <EditModal data={pageData} onSave={handleSave} onClose={() => setModalOpen(false)} saving={saving} />
      )}

      <div className="about_bg_color">
        <div className="img_carrier">
          <img src={bannerImg} alt="" />
        </div>
        <div className="about-page">
          <AboutUs data={pageData} />
          <IMSPolicy data={pageData} />
        </div>
      </div>
    </>
  );
}
